import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type NotificationType = "INFO" | "WARNING" | "URGENT" | "SUCCESS";
export type NotificationCategory =
  | "TASK_DUE"
  | "PROCEDURE_DUE"
  | "TAX_OBLIGATION_DUE"
  | "INVOICE_OVERDUE"
  | "LEGAL_HEARING"
  | "SECURITY_ALERT"
  | "DOCUMENT_EXPIRING";

export interface NotificationRecord {
  id: string;
  type: NotificationType;
  category: NotificationCategory;
  title: string;
  message: string;
  entityType: string | null;
  entityId: string | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

/** Ruta interna a la que debería llevar un clic sobre la notificación, según su categoría. */
export const NOTIFICATION_CATEGORY_ROUTE: Record<NotificationCategory, string> = {
  TASK_DUE: "/tasks",
  PROCEDURE_DUE: "/procedures",
  TAX_OBLIGATION_DUE: "/taxes",
  INVOICE_OVERDUE: "/invoices",
  LEGAL_HEARING: "/legal-cases",
  // Fase 23: alerta de acción sensible (borrado o cambio de permisos) — lleva al log de auditoría.
  SECURITY_ALERT: "/audit",
  // "Alertas de vencimientos": documento del cliente por vencer — lleva a su ficha.
  DOCUMENT_EXPIRING: "/documents",
};

/**
 * Misma tabla, pero para cuando quien la recibe es un usuario del Portal
 * del cliente (Fase 19). SECURITY_ALERT nunca se genera para usuarios de
 * Portal (solo para ADMIN internos, ver audit.service.ts), pero se incluye
 * igual para que el Record quede completo y el mapeo no falle en tiempo de
 * compilación si algún día cambia esa regla de negocio.
 */
export const NOTIFICATION_CATEGORY_PORTAL_ROUTE: Record<NotificationCategory, string> = {
  TASK_DUE: "/portal",
  PROCEDURE_DUE: "/portal/procedures",
  TAX_OBLIGATION_DUE: "/portal",
  INVOICE_OVERDUE: "/portal/invoices",
  LEGAL_HEARING: "/portal/legal-cases",
  SECURITY_ALERT: "/portal",
  DOCUMENT_EXPIRING: "/portal/documents",
};

interface ListNotificationsParams {
  page?: number;
  pageSize?: number;
  unreadOnly?: boolean;
}

export function useNotifications(params: ListNotificationsParams = {}) {
  return useQuery({
    queryKey: ["notifications", params],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<NotificationRecord[]>>("/notifications", { params });
      return data;
    },
    // Refresca periódicamente para reflejar lo que el job de BullMQ genera en segundo plano.
    refetchInterval: 60_000,
  });
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<{ count: number }>>("/notifications/unread-count");
      return data.data.count;
    },
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<NotificationRecord>>(`/notifications/${id}/mark-read`);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<ApiSuccessResponse<{ updated: number }>>("/notifications/mark-all-read");
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/notifications/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
}
